import type {
  ClientConfig,
  ClientIndicators,
  ControlLabel,
  DashboardMode,
  PendingGroup,
  ProcessedCard,
  StatusLevel,
  TrelloCard,
} from '@/types'
import {
  classifyLabels,
  CONTROL_DISPLAY_NAMES,
  isDoneProduction,
  isDoneScheduling,
  resolvePrimaryControlLabel,
} from './labels'
import { extractContentDate, isInMonth } from './dateParser'
import { getPreviousMonth } from './operations'
import { getCycleProgress, getSmartStatus } from './status'

// ─── Card processor ──────────────────────────────────────────────────────────

export function processCard(
  card: TrelloCard,
  _selectedMonth: number,
  selectedYear: number,
  mode: DashboardMode
): ProcessedCard {
  const { formatLabels, controlLabels, unknownLabels } = classifyLabels(card.labels)
  const contentDate = extractContentDate(card.name, card.due, selectedYear)
  const primaryControlLabel = resolvePrimaryControlLabel(controlLabels, unknownLabels, mode)

  return {
    id: card.id,
    name: card.name,
    shortUrl: card.shortUrl,
    contentDate,
    due: card.due,
    formatLabels,
    controlLabels,
    primaryControlLabel,
    rawLabels: card.labels.map((l) => l.name).filter(Boolean),
  }
}

// ─── Month filter ────────────────────────────────────────────────────────────

export function filterCardsByMonth(
  cards: ProcessedCard[],
  month: number,
  year: number
): ProcessedCard[] {
  return cards.filter((card) => isInMonth(card.contentDate, month, year))
}

// ─── Grouping helpers ─────────────────────────────────────────────────────────

const PRODUCTION_PENDING_ORDER: ControlLabel[] = [
  'aguardando-material',
  'gravar',
  'video',
  'design',
  'revisao',
  'outros',
  'sem-etiqueta',
]

const SCHEDULING_PENDING_ORDER: ControlLabel[] = [
  'finalizado',
  'aguardando-material',
  'gravar',
  'video',
  'design',
  'revisao',
  'outros',
  'sem-etiqueta',
]

function groupPendingCards(
  pendingCards: ProcessedCard[],
  mode: DashboardMode
): PendingGroup[] {
  const order = mode === 'production' ? PRODUCTION_PENDING_ORDER : SCHEDULING_PENDING_ORDER

  const map = new Map<ControlLabel, ProcessedCard[]>()
  for (const label of order) {
    map.set(label, [])
  }

  for (const card of pendingCards) {
    const key = card.primaryControlLabel
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(card)
  }

  return order
    .filter((label) => (map.get(label)?.length ?? 0) > 0)
    .map((label) => ({
      label,
      displayName: CONTROL_DISPLAY_NAMES[label],
      cards: map.get(label)!,
    }))
}

// ─── Main calculator ──────────────────────────────────────────────────────────

export function calculateIndicators(
  client: ClientConfig,
  rawCards: TrelloCard[],
  selectedMonth: number,
  selectedYear: number,
  mode: DashboardMode,
  error?: string
): ClientIndicators {
  if (error) {
    return {
      client,
      totalCards: 0,
      doneCards: 0,
      pendingCards: 0,
      percentage: 0,
      smartStatus: 'bom',
      pendingGroups: [],
      allCards: [],
      error,
    }
  }

  // Process all cards with mode-aware primary label resolution
  const processed = rawCards.map((card) =>
    processCard(card, selectedMonth, selectedYear, mode)
  )

  // Filter to the selected month
  const monthCardsAll = filterCardsByMonth(processed, selectedMonth, selectedYear)

  // "Postar manualmente" cards are posted on the day by hand, not scheduled in
  // advance — they're excluded entirely from Agendamento (not counted, not
  // pending). In Produção they still count normally (and are auto-done, see
  // isDoneProduction).
  const monthCards =
    mode === 'scheduling'
      ? monthCardsAll.filter((c) => !c.controlLabels.includes('postar-manualmente'))
      : monthCardsAll

  // Classify done vs pending
  const isDone =
    mode === 'production'
      ? (c: ProcessedCard) => isDoneProduction(c.controlLabels)
      : (c: ProcessedCard) => isDoneScheduling(c.controlLabels)

  const doneCards = monthCards.filter(isDone)
  const pendingCards = monthCards.filter((c) => !isDone(c))

  const total = monthCards.length
  const done = doneCards.length
  const percentage = total === 0 ? 0 : Math.round((done / total) * 100)

  // Production cycle = the month BEFORE the selected content month.
  const { month: cycleMonth, year: cycleYear } = getPreviousMonth(selectedMonth, selectedYear)
  const cycleProgress = getCycleProgress(cycleMonth, cycleYear)
  const smartStatus = getSmartStatus(percentage, total, cycleProgress)

  return {
    client,
    totalCards: total,
    doneCards: done,
    pendingCards: pendingCards.length,
    percentage,
    smartStatus,
    pendingGroups: groupPendingCards(pendingCards, mode),
    allCards: monthCards,
  }
}

// ─── Status level ─────────────────────────────────────────────────────────────

export function getStatusLevel(percentage: number): StatusLevel {
  if (percentage >= 80) return 'green'
  if (percentage >= 50) return 'yellow'
  return 'red'
}

// ─── Summary aggregator ───────────────────────────────────────────────────────

export function aggregateSummary(indicators: ClientIndicators[]): {
  total: number
  done: number
  pending: number
  percentage: number
} {
  const valid = indicators.filter((i) => !i.error)
  const total = valid.reduce((s, i) => s + i.totalCards, 0)
  const done = valid.reduce((s, i) => s + i.doneCards, 0)
  const pending = valid.reduce((s, i) => s + i.pendingCards, 0)
  const percentage = total === 0 ? 0 : Math.round((done / total) * 100)
  return { total, done, pending, percentage }
}
